import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import EnumsService from '../../Services/enums.service';
import SubAccountantService from '../../Services/subAccountant.service';
import AssetService from '../../Services/assets.service';

const isAddItemOption = (item) => item?.value1 === 'AddItem';

const upsertCardInList = (list = [], card, previousCardId, keepAddItemFirst = false) => {
    const idsToReplace = new Set(
        [card?.cardId, previousCardId]
            .filter((id) => id != null && id !== '')
            .map(String)
    );
    const source = Array.isArray(list) ? list : [];
    const filtered = source.filter(
        (item) => isAddItemOption(item) || !idsToReplace.has(String(item?.cardId))
    );
    if (keepAddItemFirst) {
        const addItem = filtered.find(isAddItemOption);
        const rest = filtered.filter((item) => !isAddItemOption(item));
        return addItem ? [addItem, ...rest, card] : [...rest, card];
    }
    return [...filtered, card];
};

/**
 * Fetches all document page enums in parallel (sort codes, account cards, client items,
 * pal codes, assets, asset groups, sub-accountants, accountant) and stores by caseId.
 */
export const fetchDocumentEnums = createAsyncThunk(
    'documentEnums/fetch',
    async (
        {
            caseId,
            accountantId,
            token,
            addItemLabel = 'Add Item',
            addAccountCardLabel = 'Add Account Card',
            clientName,
            forceRefetch = false,
        },
        { rejectWithValue, getState }
    ) => {
        const cached = getState()?.documentEnumsSlice?.byCaseId?.[caseId];
        if (cached && !forceRefetch) {
            return { caseId, ...cached };
        }
        try {
            const [
                codeIdResponse,
                accountsResponse,
                clientItemsResponse,
                palCodesResponse,
                assetResponse,
                assetGroupResponse,
                subAccountantResult,
                accountantResult,
            ] = await Promise.all([
                EnumsService.GetSortCodesByAccountantId(accountantId, token),
                EnumsService.GetAccountCardsByCaseId(caseId, token),
                EnumsService.GetClientItemsByCaseId(caseId, token),
                EnumsService.GetPalCodesByAccountantId(accountantId, token),
                AssetService.GetAllAssets(caseId, token),
                AssetService.GetAllAssetGroups(caseId, token),
                SubAccountantService.GetSubAccountantByAccountantId(token, accountantId),
                SubAccountantService.GetAccountantById(token, accountantId),
            ]);

            const codeIdData = codeIdResponse.ok ? await codeIdResponse.json() : [];
            const accountsData = accountsResponse.ok ? await accountsResponse.json() : [];
            const clientItemsData = clientItemsResponse.ok ? await clientItemsResponse.json() : [];
            const palCodesData = palCodesResponse.ok ? await palCodesResponse.json() : [];

            const clientItemsWithAdd = [{ makat: 'add-item', name: addItemLabel }, ...clientItemsData];
            const accountCardOptions = [{ name: addAccountCardLabel, value1: 'AddItem' }, ...accountsData];
            const formattedPalCodeOptions = (palCodesData || []).map((palCode) => ({
                label: palCode.palCodeId,
                value: palCode.palCodeId,
            }));

            const enhancedAccountant = accountantResult
                ? { ...accountantResult, subAccountantId: accountantResult.accountantId }
                : null;
            const combinedSubAccountants = enhancedAccountant ? [enhancedAccountant, ...(subAccountantResult || [])] : subAccountantResult || [];
            if (clientName) {
                combinedSubAccountants.push({ subAccountantId: caseId, name: clientName });
            }
            const subAccountantOptions = combinedSubAccountants.map((acc) => ({
                value: acc.subAccountantId,
                label: acc.name,
            }));

            return {
                caseId,
                sortCodeIdOptions: codeIdData,
                accountCardOptions,
                creditAccountOptions: accountsData,
                debitAccountOptions: accountsData,
                accountCardEditDownloadOptions: accountsData,
                clientItems: clientItemsWithAdd,
                formattedPalCodeOptions,
                assets: assetResponse?.success ? assetResponse.data : [],
                assetGroups: assetGroupResponse?.success ? assetGroupResponse.data : [],
                subAccountants: combinedSubAccountants,
                subAccountantOptions,
                accountant: enhancedAccountant,
                assetsError: !assetResponse?.success || !assetGroupResponse?.success,
                subAccountantsError: !subAccountantResult && !accountantResult,
            };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

const initialState = {
    byCaseId: {},
    loading: {},
    error: {},
};

const documentEnumsSlice = createSlice({
    name: 'documentEnums',
    initialState,
    reducers: {
        clearDocumentEnums: (state, action) => {
            const caseId = action.payload;
            if (caseId) {
                delete state.byCaseId[caseId];
                delete state.loading[caseId];
                delete state.error[caseId];
            } else {
                state.byCaseId = {};
                state.loading = {};
                state.error = {};
            }
        },
        upsertAccountCard: (state, action) => {
            const { caseId, card, previousCardId = null } = action.payload || {};
            if (!caseId || card?.cardId == null || card.cardId === '') return;
            const cached = state.byCaseId[caseId];
            if (!cached) return;

            cached.accountCardOptions = upsertCardInList(
                cached.accountCardOptions,
                card,
                previousCardId,
                true
            );
            cached.creditAccountOptions = upsertCardInList(
                cached.creditAccountOptions,
                card,
                previousCardId
            );
            cached.debitAccountOptions = upsertCardInList(
                cached.debitAccountOptions,
                card,
                previousCardId
            );
            cached.accountCardEditDownloadOptions = upsertCardInList(
                cached.accountCardEditDownloadOptions,
                card,
                previousCardId
            );
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchDocumentEnums.pending, (state, action) => {
                const caseId = action.meta.arg.caseId;
                state.loading[caseId] = true;
                state.error[caseId] = null;
            })
            .addCase(fetchDocumentEnums.fulfilled, (state, action) => {
                const { caseId, ...rest } = action.payload;
                state.byCaseId[caseId] = rest;
                state.loading[caseId] = false;
                state.error[caseId] = null;
            })
            .addCase(fetchDocumentEnums.rejected, (state, action) => {
                const caseId = action.meta.arg?.caseId;
                if (caseId) {
                    state.loading[caseId] = false;
                    state.error[caseId] = action.payload;
                }
            });
    },
});

export const { clearDocumentEnums, upsertAccountCard } = documentEnumsSlice.actions;

export const selectDocumentEnums = (state, caseId) =>
    (caseId && state.documentEnumsSlice?.byCaseId?.[caseId]) || null;

export const selectDocumentEnumsLoading = (state, caseId) =>
    (caseId && state.documentEnumsSlice?.loading?.[caseId]) || false;

export const selectDocumentEnumsError = (state, caseId) =>
    (caseId && state.documentEnumsSlice?.error?.[caseId]) || null;

export default documentEnumsSlice.reducer;
